import React, { useEffect, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import Select from "react-select";
import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";
import NotificationEventTypes from "../../constants/notificationEventsEnum";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png"; // Update with your logo path

function StudentWithdrawal() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const userId = localStorage.getItem("userId");
  const roleId = localStorage.getItem("roleId");

  const [levelsData, setLevelsData] = useState([]);
  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedClassSectionId, setSelectedClassSectionId] = useState(
    roleId == "9" || roleId == "10" ? "0" : null
  );
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [centersData, setCentersData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [classSectionsData, setClassSectionsData] = useState([]);
  const [studentsData, setStudentsData] = useState([]);
  const [withdrawalTypesData, setWithdrawalTypesData] = useState([]);
  const [withdrawnStudentsData, setWithdrawnStudentsData] = useState([]);
  const [triggerRerender, setTriggerRerender] = useState(false);

  const [customDropdownName, setCustomDropdownName] = useState("");
  const [activityCategoryData, setActivityCategoryData] = useState([]);
  const [activityVenueData, setActivityVenueData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      id: 0,
      centerId: "",
      classId: "",
      classSectionId: "",
      studentId: "",
      withdrawalTypeId: "",
      withdrawalType: "",
      reason: "",
      comments: "",
      withdrawalDate: new Date().toISOString().split("T")[0],
      lastDateOfAttendance: "",
      createdBy: localStorage.getItem("userId"),
      processedBy: localStorage.getItem("userId"),
      iTRemarks: "",
      libraryRemarks: "",
      academicRemarks: "",
      accountsRemarks: "",
      transportRemarks: "",
    },
  });

  const withdrawalDate = useWatch({ control, name: "withdrawalDate" });

  const getFutureDate = (date, days) => {
    if (!date) return "";
    const newDate = new Date(date);
    if (isNaN(newDate.getTime())) return "";
    newDate.setDate(newDate.getDate() + days);
    return newDate.toISOString().split("T")[0];
  };

  useEffect(() => {
    if (withdrawalDate) {
      const minLastDate = getFutureDate(withdrawalDate, 28);
      const currentLastDate = getValues("lastDateOfAttendance");
      if (!currentLastDate || currentLastDate < minLastDate) {
        setValue("lastDateOfAttendance", minLastDate);
      }
    } else {
      setValue("lastDateOfAttendance", "");
    }
  }, [withdrawalDate, setValue, getValues]);

  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(response?.commonSettingDetail || []);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  const fetchCenters = async () => {
    try {
      const response = await get(
        `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCentersData(response || []);
      if (savedCenterId) {
        const selectedCenter = response.find(
          (center) => center.id === parseInt(savedCenterId, 10)
        );
        if (selectedCenter) {
          setValue("centerId", {
            value: selectedCenter.id,
            label: selectedCenter.name,
          });
          setCenterId(selectedCenter.id);
          fetchClassesByCenterId(selectedCenter.id);
        }
      }
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  };

  const fetchClassesByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const response = await get(
          `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClassesData(response || []);
      } else {
        setClassesData([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClassesData([]);
    }
  };

  const fetchClassSectionsByCenterId = async (centerId, classId) => {
    try {
      if (centerId && classId) {
        const response = await get(
          `ClassSection/GetAllClassSectionByCenterId?centerId=${centerId}&classId=${classId}&userId=${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClassSectionsData(response || []);
      } else {
        setClassSectionsData([]);
      }
    } catch (error) {
      console.error("Error fetching class sections:", error);
      setClassSectionsData([]);
    }
  };

  const fetchStudentsByClassSectionId = async () => {
    try {
      if (selectedClassSectionId && selectedClassSectionId !== "0") {
        const response = await get(
          `Student/GetStudentByClassSectionIdAsync?classSectionId=${selectedClassSectionId}&userId=${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStudentsData(response || []);
      } else {
        setStudentsData([]);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      setStudentsData([]);
    }
  };

  const fetchWithdrawnStudents = async (centerId) => {
    try {
      const response = await get(
        `Withdrawal/GetWithdrawalByCenterId?centerId=${centerId}&userId=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWithdrawnStudentsData(response?.withdrawal || []);
    } catch (error) {
      console.error("Error fetching withdrawn students:", error);
      setWithdrawnStudentsData([]);
    }
  };

  const fetchWithdrawalTypes = async () => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=withdrawalType`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWithdrawalTypesData(response?.commonSettingDetail || []);
    } catch (error) {
      console.error("Error fetching withdrawal types:", error);
    }
  };

  useEffect(() => {
    fetchCenters();
    fetchWithdrawalTypes();
  }, []);

  useEffect(() => {
    if (centerId) {
      fetchWithdrawnStudents(centerId);
      fetchClassesByCenterId(centerId);
    } else {
      setWithdrawnStudentsData([]);
      setClassesData([]);
    }
  }, [centerId]);

  useEffect(() => {
    if (selectedClassSectionId && selectedClassSectionId !== "0") {
      fetchStudentsByClassSectionId();
    } else {
      setStudentsData([]);
    }
  }, [selectedClassSectionId]);

  const handleCustomDropdownSetting = (dropdownName) => {
    setCustomDropdownName(dropdownName);
    setIsCustomDropdownValuesModalVisible(true);
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false, 
        size: 80,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "studentName",
        header: "Student",
        // size: 150,
      },
      {
        accessorKey: "withdrawalType",
        header: "Withdrawal Type",
        // size: 150,
      },
      {
        accessorKey: "withdrawalDate",
        header: "Application Date",
        // size: 150,
        Cell: ({ row }) => row.original.withdrawalDate?.split("T")[0],
      },
      {
        accessorKey: "lastDateOfAttendance",
        header: "Effective Date",
        // size: 150,
        Cell: ({ row }) => row.original.lastDateOfAttendance?.split("T")[0],
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) =>
          row.original.submissionDate ? (
            <span className="badge bg-success fs-10">Withdrawn</span>
          ) : (
            <span className="badge bg-warning fs-10">Pending</span>
          ),
      },
      // {
      //   name: "Action",
      //   width: "70px",
      //   selector: (row) => (
      //     <div className="d-flex gap-1">
      //       <button
      //         type="button"
      //         className="btn p-0"
      //         onClick={() => handleEditClick(row.id)}
      //       >
      //         <i className="fas fa-pencil"></i>
      //       </button>
      //     </div>
      //   ),
      // },
    ],
    []
  );

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToExcel = () => {
    const dataToExport = withdrawnStudentsData.map((row, index) => ({
      "Sr No": index + 1,
      Student: row.studentName,
      "Withdrawal Type": row.withdrawalType,
      "Application Date": row.withdrawalDate?.split("T")[0],
      "Effective Date": row.lastDateOfAttendance?.split("T")[0],
      Status: row.submissionDate ? "Withdrawn" : "Pending",
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Withdrawal Students");
    XLSX.writeFile(workbook, "StudentWithdrawal.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    
    const roleIdLoggedIn = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleIdLoggedIn == "2" ? orgLogo : roleIdLoggedIn == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Student Withdrawal List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "mrt-row-numbers" && column.accessorKey !== "action"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = withdrawnStudentsData.map((row, index) => {
      return [
        index + 1,
        row.studentName,
        row.withdrawalType,
        row.withdrawalDate?.split("T")[0],
        row.lastDateOfAttendance?.split("T")[0],
        row.submissionDate ? "Withdrawn" : "Pending",
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      startY: titleY + 10,
      theme: "striped",
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("StudentWithdrawalList.pdf");
  };

  const handleEditClick = async (rowId) => {
    try {
      const response = await get(
        `Withdrawal/GetWithdrawalById?withdrawalId=${rowId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const withdrawalSingleItem = response?.withdrawal;

      const selectedCenter = centersData.find(
        (center) => center.id === withdrawalSingleItem.centerId
      );
      const selectedClass = classesData.find(
        (classItem) => classItem.id === withdrawalSingleItem.classId
      );
      const selectedClassSection = classSectionsData.find(
        (classSection) =>
          classSection.id === withdrawalSingleItem.classSectionId
      );
      const selectedWithdrawalType = withdrawalTypesData.find(
        (withdrawalType) =>
          withdrawalType.id === withdrawalSingleItem.withdrawalTypeId
      );

      const studentsResponse = await get(
        `Student/GetStudentByClassSectionIdAsync?classSectionId=${withdrawalSingleItem.classSectionId}&userId=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const selectedStudent = studentsResponse.find(
        (student) => student.id === withdrawalSingleItem.studentId
      );

      reset({
        id: withdrawalSingleItem.id,
        centerId: {
          value: selectedCenter?.id || "",
          label: selectedCenter?.name || "",
        },
        classId: {
          value: selectedClass?.id || "",
          label: selectedClass?.name || "",
        },
        classSectionId: {
          value: selectedClassSection?.id || "",
          label: selectedClassSection?.name || "",
        },
        studentId: {
          value: selectedStudent?.id || "",
          label:
            selectedStudent?.studentName + " — " + selectedStudent.code || "",
        },
        withdrawalType: {
          value: selectedWithdrawalType?.id || "",
          label: selectedWithdrawalType?.value || "",
        },
        reason: withdrawalSingleItem.reason,
        comments: withdrawalSingleItem.comments,
        withdrawalDate: withdrawalSingleItem.withdrawalDate?.split("T")[0],
        lastDateOfAttendance:
          withdrawalSingleItem.lastDateOfAttendance?.split("T")[0],
        createdBy: withdrawalSingleItem.createdBy,
        processedBy: withdrawalSingleItem.processedBy,
        updatedBy: localStorage.getItem("userId"),
        iTRemarks: "",
        libraryRemarks: "",
        academicRemarks: "",
        accountsRemarks: "",
        transportRemarks: "",
      });

      setCenterId(withdrawalSingleItem.centerId);
      setSelectedClassId(withdrawalSingleItem.classId);
      setSelectedClassSectionId(withdrawalSingleItem.classSectionId);
      setSelectedStudentId(withdrawalSingleItem.studentId);
      fetchClassSectionsByCenterId(
        withdrawalSingleItem.centerId,
        withdrawalSingleItem.classId
      );
      fetchStudentsByClassSectionId();
    } catch (error) {
      console.error("Error fetching withdrawal data for edit:", error);
    }
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      toast.error("Unauthorized access.");
      return;
    }

    const formdata = {
      id: data.id,
      centerId: data?.centerId?.value,
      classSectionId: data?.classSectionId?.value || selectedClassSectionId,
      studentId: data?.studentId?.value,
      withdrawalTypeId: data?.withdrawalType?.value,
      withdrawalType: data?.withdrawalType?.label,
      reason: data.reason,
      comments: data.comments,
      withdrawalDate: data.withdrawalDate,
      lastDateOfAttendance: data.lastDateOfAttendance,
      createdBy: data.createdBy,
      processedBy: data.processedBy,
      updatedBy: localStorage.getItem("userId"),
      iTRemarks: data.iTRemarks,
      libraryRemarks: data.libraryRemarks,
      academicRemarks: data.academicRemarks,
      accountsRemarks: data.accountsRemarks,
      transportRemarks: data.transportRemarks,
      FormId: 58,
      NotificationEventTypeIds: NotificationEventTypes.ADD.toString(),
    };

    try {
      const response = await post(
        `Withdrawal/AddUpdateWithdrawalAsync`,
        formdata,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        fetchWithdrawnStudents(centerId);
        reset({
          id: 0,
          centerId: getValues("centerId"),
          classId: "",
          classSectionId: "",
          studentId: "",
          withdrawalTypeId: "",
          withdrawalType: "",
          reason: "",
          comments: "",
          withdrawalDate: new Date().toISOString().split("T")[0],
          lastDateOfAttendance: "",
          createdBy: localStorage.getItem("userId"),
          processedBy: localStorage.getItem("userId"),
          iTRemarks: "",
          libraryRemarks: "",
          academicRemarks: "",
          accountsRemarks: "",
          transportRemarks: "",
        });
        setSelectedClassId(null);
        setSelectedClassSectionId(roleId == "9" || roleId == "10" ? "0" : null);
        setSelectedStudentId(null);
        setClassSectionsData([]);
        setStudentsData([]);
      } else {
        toast.error(response.message || "Error!");
      }
    } catch (error) {
      console.error("Error saving withdrawal:", error);
      toast.error("Failed to save withdrawal");
    }
  };

  const searchFilteredData = withdrawnStudentsData.filter((item) =>
    item.studentName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Student Withdrawal</h2>
      </div>

      <form
        className="row g-3 new-form-style"
        onSubmit={handleSubmit(saveData)}
      >
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6 d-none">
                  <label className="form-label form-label-new">
                    Center
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="centerId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={centersData.map((center) => ({
                          value: center.id,
                          label: center.name,
                        }))}
                        isDisabled={savedCenterId !== "0"}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("centerId", null);
                            setCenterId(null);
                            setClassesData([]);
                            setClassSectionsData([]);
                            setStudentsData([]);
                            setValue("classId", null);
                            setValue("classSectionId", null);
                            setValue("studentId", null);
                            setSelectedClassId(null);
                            setSelectedClassSectionId(null);
                            setSelectedStudentId(null);
                          } else {
                            setValue("centerId", selectedOption);
                            setCenterId(selectedOption.value);
                            fetchClassesByCenterId(selectedOption.value);
                            setClassSectionsData([]);
                            setStudentsData([]);
                            setValue("classId", null);
                            setValue("classSectionId", null);
                            setValue("studentId", null);
                            setSelectedClassId(null);
                            setSelectedClassSectionId(null);
                            setSelectedStudentId(null);
                          }
                          trigger("centerId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.centerId && (
                    <small className="text-danger">
                      {errors.centerId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Classes
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="classId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={classesData.map((classItem) => ({
                          value: classItem.id,
                          label: classItem.name,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("classId", null);
                            setSelectedClassId(null);
                            setClassSectionsData([]);
                            setStudentsData([]);
                            setValue("classSectionId", null);
                            setValue("studentId", null);
                            setSelectedClassSectionId(null);
                            setSelectedStudentId(null);
                          } else {
                            setValue("classId", selectedOption);
                            setSelectedClassId(selectedOption.value);
                            fetchClassSectionsByCenterId(
                              centerId,
                              selectedOption.value
                            );
                            setStudentsData([]);
                            setValue("classSectionId", null);
                            setValue("studentId", null);
                            setSelectedClassSectionId(null);
                            setSelectedStudentId(null);
                          }
                          trigger("classId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                      />
                    )}
                  />
                  {errors.classId && (
                    <small className="text-danger">
                      {errors.classId.message}
                    </small>
                  )}
                </div>
                <div
                  className={`col-md-6 ${
                    selectedClassSectionId == "0" ? "d-none" : ""
                  }`}
                >
                  <label className="form-label form-label-new">
                    Class - Section
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="classSectionId"
                    control={control}
                    rules={{
                      required:
                        selectedClassSectionId === "0"
                          ? false
                          : "This field is required.",
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={classSectionsData.map((classSection) => ({
                          value: classSection.id,
                          label: classSection.name,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("classSectionId", null);
                            setSelectedClassSectionId(null);
                            setStudentsData([]);
                            setValue("studentId", null);
                            setSelectedStudentId(null);
                          } else {
                            setValue("classSectionId", selectedOption);
                            setSelectedClassSectionId(selectedOption.value);
                            setValue("studentId", null);
                            setSelectedStudentId(null);
                          }
                          trigger("classSectionId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isDisabled={selectedClassSectionId == "0"}
                        isClearable
                      />
                    )}
                  />
                  {errors.classSectionId && (
                    <small className="text-danger">
                      {errors.classSectionId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-12">
                  <label className="form-label form-label-new">
                    Student<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="studentId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={studentsData.map((student) => ({
                          value: student.id,
                          label: `${student.studentName} — ${student.code} — ${student.className}(${student.sectionName})`,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setSelectedStudentId(null);
                            setValue("studentId", null);
                          } else {
                            setSelectedStudentId(
                              selectedOption ? selectedOption.value : null
                            );
                            setValue("studentId", selectedOption);
                          }
                          trigger("studentId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                      />
                    )}
                  />
                  {errors.studentId && (
                    <small className="text-danger">
                      {errors.studentId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-12">
                  <label className="form-label form-label-new">
                    Withdrawal Type<span className="text-danger fs-9">*</span>
                  </label>
                  <div className="d-flex gap-2">
                    <div className="w-100">
                      <Controller
                        name="withdrawalType"
                        control={control}
                        rules={{ required: "This field is required." }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={withdrawalTypesData.map(
                              (withdrawalType) => ({
                                value: withdrawalType.id,
                                label: withdrawalType.value,
                              })
                            )}
                            onChange={(selectedOption, actionMeta) => {
                              if (actionMeta.action === "clear") {
                                setValue("withdrawalType", null);
                              } else {
                                setValue("withdrawalType", selectedOption);
                              }
                              trigger("withdrawalType");
                            }}
                            className="react-select-style"
                            classNamePrefix="react-select-style-pref"
                            isClearable
                          />
                        )}
                      />
                      {errors.withdrawalType && (
                        <small className="text-danger">
                          {errors.withdrawalType.message}
                        </small>
                      )}
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        style={{ padding: "5.5px 10px" }}
                        onClick={() =>
                          handleCustomDropdownSetting("withdrawalType")
                        }
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Application Date
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("withdrawalDate", {
                      required: "This field is required.",
                    })}
                    type="date"
                    className="form-control"
                    min={new Date().toISOString().split("T")[0]}
                  />
                  {errors.withdrawalDate && (
                    <small className="text-danger">
                      {errors.withdrawalDate.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Effective Date
                  </label>
                  <input
                    {...register("lastDateOfAttendance", {
                      validate: (value) =>
                        !withdrawalDate ||
                        value >= getFutureDate(withdrawalDate, 28) ||
                        "Last date must be at least 28 days after withdrawal date.",
                    })}
                    type="date"
                    className="form-control"
                    min={
                      withdrawalDate ? getFutureDate(withdrawalDate, 28) : ""
                    }
                    disabled={!withdrawalDate}
                  />
                  {errors.lastDateOfAttendance && (
                    <small className="text-danger">
                      {errors.lastDateOfAttendance.message}
                    </small>
                  )}
                </div>
                <div className="col-md-12">
                  <label className="form-label form-label-new">
                    Withdrawal Reason
                  </label>
                  <textarea
                    className="form-control"
                    {...register("reason")}
                  ></textarea>
                  {errors.reason && (
                    <small className="text-danger">
                      {errors.reason.message}
                    </small>
                  )}
                </div>
                <div className="col-md-12">
                  <label className="form-label form-label-new">Comments</label>
                  <textarea
                    className="form-control"
                    {...register("comments")}
                  ></textarea>
                  {errors.comments && (
                    <small className="text-danger">
                      {errors.comments.message}
                    </small>
                  )}
                </div>
                <div className="col-12">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span
                        className="spinner-border spinner-custom-size me-1"
                        aria-hidden="true"
                      ></span>
                    ) : null}
                    <span role="status">Save</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <h5 className="mb-2">Withdrawal Requests</h5>
                {/* <div className="ms-auto">
                  <div className="d-flex align-items-center gap-1">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div> */}
              </div>
              <div className="table-container fixed-last-column-table">
                <CustomDatatableGrid
                  columns={columns}
                  data={searchFilteredData}
                  onExportExcel={exportToExcel}
                  onExportPDF={exportToPDF}
                  enableSearching={false}
                />
              </div>
            </div>
          </div>
        </div>
      </form>

      {isCustomDropdownValuesModalVisible && (
        <DropdownWithCustomValuesModal
          dropdownName={customDropdownName}
          onClose={() => {
            setIsCustomDropdownValuesModalVisible(false);
            if (customDropdownName === "withdrawalType") {
              fetchCommonSettings("withdrawalType", setWithdrawalTypesData);
            }
          }}
        />
      )}
    </>
  );
}

export default StudentWithdrawal;
